import React, { useContext, useEffect, useState } from "react";
import { scroller } from "react-scroll";
import {
  FormatValueCalc,
  MaterialValueCalc,
  ProcessingValueCalc,
} from "./CalcOut";
import { change, formValueSelector, reduxForm } from "redux-form";
import Material from "./material";
import View from "./view/view";
import Depth from "./depth/depth";
import Form from "./format/Format";
import Processing from "./processing/Processing";
import Additionally from "./additionally/Additionally";
import Shadow from "./Shadow";
import { connect } from "react-redux";
import { NavContext, showedNav } from "../../context/NavState";
import { IAdditional, IPriceMultiply, IProcessing, IType } from "../../types";
import { TextContext } from "../../context/TextContext";
import { ApiData } from "../../api";

type ICalcStaticFields =
  | "material"
  | "depth"
  | "view"
  | "format"
  | "formatMM1"
  | "formatMM2"
  | "processing"
  | "processingValue"
  | string;

export type ChoseRecord = Record<
  string,
  { selected: string; type?: "add" | "mul"; price: number }
>;

let CalcForm = (props) => {
  const {
    hasMaterial,
    shadows,
    shadowFalse,
    hasView,
    hasDepth,
    hasFormat,
    hasFormatValue,
    hasProcessing,
    hasProcessingValue,
    reloadBeforeBasket,
    handleSubmit,
    checkSum,
    setCheckSum,
  } = props;
  const [calc, setCalc] = useState([] as IType[]);
  const { origNavigation, setNavigationContext } = useContext(NavContext);
  const { data: setting } = useContext(TextContext);
  const [processing, setProcessing] = useState([] as IProcessing[]);
  const [resetFlds, setResetFlds] = useState([] as string[]);
  const [additionally, setAdditionally] = useState([] as IAdditional[]);
  const [multiply, setMultiply] = useState([] as IPriceMultiply[]);
  const [additionalChoose, setAdditionalChoose] = useState({} as ChoseRecord);
  const [uncheck, setUncheck] = useState(false);

  const views = hasMaterial
    ? calc.find((el) => el.name === hasMaterial)?.views
    : [];
  const depth =
    hasView && views?.length
      ? views.find((el) => el.name === hasView)?.prices
      : [];

  useEffect(() => {
    ApiData.calc().then((res) => {
      if (!res?.length) return;
      showedNav.add("calculator");
      setNavigationContext(origNavigation.filter((el) => showedNav.has(el.to)));
      const filterTypeWithoutView = res.filter((el) => el.views?.length);
      if (!filterTypeWithoutView?.length) return;
      const calc = filterTypeWithoutView.filter(
        (type) =>
          type.views?.filter(
            (view) =>
              view.prices?.filter((price) => !!price.depth?.millimetre)?.length,
          )?.length,
      );
      if (!calc?.length) return;
      setCalc(calc);
    });
    ApiData.processing().then((res) => {
      if (!res?.length) return;
      setProcessing(res);
    });
    ApiData.additionally().then((res) => {
      if (!res?.length) return;
      setResetFlds([
        ...res.map((el) => el.name),
        ...res.map((el) => el.additional_choises.map((c) => c.name)).flat(),
      ]);
      setAdditionally(res);
    });
    ApiData.multiply().then((res) => {
      if (!res?.length) return;
      setMultiply(res);
    });
  }, []);

  useEffect(() => {
    if (reloadBeforeBasket) {
      scroller.scrollTo("basket", {
        duration: 800,
        delay: 0,
        smooth: "easeInOutQuart",
      });
      shadowFalse(0);
      resetForm([
        "material",
        "view",
        "depth",
        "format",
        "formatMM1",
        "formatMM2",
        "processing",
        "processingValue",
      ]);
    }
  }, [reloadBeforeBasket]);

  React.useEffect(() => {
    setCheckSum(Calculator());
  }, [
    hasFormat,
    hasView,
    hasDepth,
    hasProcessing,
    hasProcessingValue,
    hasFormatValue,
    additionalChoose,
  ]);

  const Calculator = () => {
    if (!calc?.length || !processing?.length) return;
    if (!hasDepth && !depth?.length) return;
    const price = depth.find((el) => el?.depth?.millimetre == hasDepth);
    if (!price) return;
    const materialPrice = price.money;
    if (!materialPrice) return;
    if (Object.values(hasFormatValue).some((el) => el && el > 9999))
      return materialPrice;
    const formatValue: number = FormatValueCalc(
      hasFormat,
      hasFormatValue.formatMM1,
      hasFormatValue.formatMM2,
    );
    const materialValue: number = MaterialValueCalc(formatValue, materialPrice);
    const processingValue: number = ProcessingValueCalc(
      processing,
      hasFormat,
      hasDepth,
      hasProcessing,
      hasFormatValue.formatMM1,
      hasFormatValue.formatMM2,
      hasProcessingValue,
    );
    let result = materialValue + processingValue;
    const typeFunc: Record<
      ChoseRecord[string]["type"],
      (el: { price: number }) => void
    > = {
      add: (el) => {
        result += el.price;
      },
      mul: (el) => {
        result += formatValue * el.price;
      },
    };
    Object.values(additionalChoose)
      .filter((el) => el)
      .forEach((el) => typeFunc[el.type]?.(el));

    if (setting.percent) result *= setting.percent;
    const multiplyFiltered = multiply.filter((m) => {
      const el = m;
      if (
        el.formatMM1 &&
        hasFormatValue.formatMM1 &&
        +hasFormatValue.formatMM1 < el.formatMM1
      )
        return false;
      if (
        el.formatMM2 &&
        hasFormatValue.formatMM2 &&
        +hasFormatValue.formatMM2 < el.formatMM2
      )
        return false;
      if (el.types.length && el.types.some((el) => el.name !== hasMaterial))
        return false;
      if (el.views.length && el.views.some((el) => el.name !== hasView))
        return false;
      return !(
        el.formats.length && el.formats.some((el) => el.name !== hasFormat)
      );
    });
    let mul: IPriceMultiply;
    if (multiplyFiltered.length) {
      for (const m of multiplyFiltered) {
        if (mul && mul.multiply > m.multiply) continue;
        else mul = m;
      }
    }
    if (mul) {
      if (mul.multiplyType === "на метры")
        result +=
          (hasFormatValue.formatMM1 / 1000) *
          ((hasFormatValue.formatMM2 ?? 1000) / 1000) *
          mul.multiply;
      if (mul.multiplyType === "на итоговую цену") result *= mul.multiply;
    }
    return Math.ceil(result);
  };
  const resetForm = (fields: ICalcStaticFields[]) => {
    for (const f of resetFlds) {
      fields?.push(f);
      fields?.push(f + "_Значение");
    }
    for (const field of fields)
      props.dispatch(change("calc", field, undefined));
    setUncheck((u) => !u);
  };

  const disableButton =
    (hasProcessing !== "Полировка" &&
      !hasProcessingValue &&
      hasProcessing !== "Без обработки") ||
    !(!!hasFormat && hasFormatValue.formatMM1 > 150 && checkSum > 1500);
  const getSumWithSpace = (v) => {
    if (!v) return "0";
    const n = v.toString();
    const separator = " ";
    return n.replace(/(\d{1,3}(?=(?:\d\d\d)+(?!\d)))/g, "$1" + separator);
  };
  const sum = getSumWithSpace(checkSum);
  const sumBeforeDiscount = (parseInt(sum.split(" ").join("")) * 1.25).toFixed(
    0,
  );
  return calc?.length ? (
    <div className="bl_4_head_text_box">
      <div className="titles">Расчет стоимости заказа онлайн</div>
      <form className="bl_4_grid_container" onSubmit={handleSubmit}>
        <Material data={calc} resetForm={resetForm} shadowFalse={shadowFalse} />
        <View
          data={views}
          resetForm={resetForm}
          shadowFalse={shadowFalse}
          shadow={shadows[1]}
        />
        <Depth
          data={depth}
          resetForm={resetForm}
          shadowFalse={shadowFalse}
          hasMaterial={hasMaterial}
          shadow={shadows[2]}
        />
        <Form
          formValue={hasFormatValue}
          resetForm={resetForm}
          hasFormat={hasFormat}
          hasProcessing={hasProcessing}
          shadowFalse={shadowFalse}
          shadow={shadows[3]}
        />
        <Processing
          resetForm={resetForm}
          hasFormat={hasFormat}
          hasDepth={hasDepth}
          proc={processing}
          hasProcessing={hasProcessing}
          shadowFalse={shadowFalse}
          shadow={shadows[4]}
        />
        <Additionally
          uncheck={uncheck}
          setAdditionalChoose={setAdditionalChoose}
          additionally={additionally}
          hasFormat={hasFormat}
          dispatch={props.dispatch}
          hasDepth={hasDepth}
          hasView={hasView}
          hasMaterial={hasMaterial}
          shadow={shadows[5]}
        />
        <div
          onClick={() => shadowFalse(8)}
          className="bl_4_grid_item bl_4_grid_item_sum"
        >
          {disableButton && <Shadow />}
          <div className="calc_title_box_sum">
            <div className="calc_title">Стоимость</div>
            <div className="calc_sumText">
              {sum !== "0" && (
                <div
                  style={{
                    textDecoration: "line-through",
                    marginRight: "15px",
                  }}
                >
                  {getSumWithSpace(sumBeforeDiscount)}
                </div>
              )}
              <div className="summa">{sum}₽</div>
            </div>
          </div>
        </div>
        <button
          disabled={disableButton}
          className={"bl_4_grid_button"}
          /*onClick={() => {
            if(window.ym)window.ym(71269705,'reachGoal','addToCart');
          }}*/
        >
          ДОБАВИТЬ В КОРЗИНУ{" "}
          {checkSum < 1500 ? "(минимальная стоимость 1500₽)" : ""}
        </button>
      </form>
    </div>
  ) : (
    <></>
  );
};

const selector = formValueSelector("calc");
CalcForm = reduxForm({
  form: "calc",
})(CalcForm);

export default connect((state) => ({
  hasMaterial: selector(state, "material"),
  hasView: selector(state, "view"),
  hasDepth: selector(state, "depth"),
  hasFormat: selector(state, "format"),
  hasFormatValue: {
    formatMM1: selector(state, "formatMM1"),
    formatMM2: selector(state, "formatMM2"),
  },
  hasProcessing: selector(state, "processing"),
  hasProcessingValue: selector(state, "processingValue"),
}))(CalcForm);
